import React, { useEffect, useRef, useState } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useUpdateWalletLimits, useNavigate } from '@utils/hooks';

import { notificationsActions, useStore, withdrawActions } from '@modules';
import { CurrencyEnum, CryptoCurrencyEnum } from '@modules/global/types';
import { NotificationTypesEnum } from '@modules/notifications/types';
import { ProtectNextEnum, VerifyNextEnum } from '@modules/types';
import { OperationsEnum } from '@modules/wallet/types';
import { GETWithdrawEstimateFeeResponse, GETWithdrawRateResponse } from '@modules/withdraw/types';

import Button from '@components/Button';
import AddressInput from '@containers/AddressInput';
import Converter, { TConverter } from '@containers/Converter';
import Limits from '@containers/Converter/Limits';
import useConverterState from '@containers/Converter/useConverterState';
import ZHDisclaimerInfo from '@containers/ZHDisclaimerInfo';

import Details from '../../../Details';
import OperationsDisabledDisclaimer from '../../OperationsDisabledDisclaimer';
import { useCustomContext } from '../../context';

import style from './Withdraw.scss';

const cn = bemCssModules(style, 'Withdraw');
const ti = getTestId('Withdraw');
const { verifyOTP, setupOTP } = ProtectNextEnum;

const Withdraw: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ user, global, withdraw, wallet, operationBlock }, dispatch] = useStore();
  const { addressLoading, loaders } = withdraw;
  const { display_options, has_withdrawal_fee } = global.currencies.config;
  const { left, right } = useCustomContext();

  const converterState = useConverterState({
    fromCurrencies: [left?.value as CurrencyEnum],
    toCurrencies: [user.data?.fiat_currency || CONST.defaultFiatCurrency],
  });
  const { fromCurrency, fromAmount, toCurrency, toAmount, showLimits, updateRates } =
    converterState;
  const rate = converterState.rate as GETWithdrawRateResponse | undefined;
  const address = withdraw.address[fromCurrency];
  const [withdrawalFee, setWithdrawalFee] = useState<GETWithdrawEstimateFeeResponse['fees']>([]);

  const totalDigits = display_options?.[fromCurrency]?.total_digits;
  const displayDigits = display_options?.[fromCurrency]?.display_digits;

  const hasCurrencyWithdrawalFee = has_withdrawal_fee[fromCurrency];
  const getError = (name: string) => withdraw.errors.data?.[name]?.[0];
  const setFee = (id?: string) => dispatch(withdrawActions.setFeeId(id));
  const clearWithdrawFee = () => setWithdrawalFee([]);
  const isValidFee = +(rate?.fee?.[rate?.fiat_currency] || '0') === 0;

  const isLoadingEstimate = loaders.getWithdrawEstimate;
  const isLoadingWithdraw = loaders.withdraw;
  const isLoadingRate = loaders.getWithdrawRate;
  const isLoadingAddress = !!isLoadingEstimate || !!addressLoading;

  const abort = useRef<() => void>(null);

  useUpdateWalletLimits({
    currency: toCurrency,
    crypto_currency: fromCurrency,
    type: OperationsEnum.withdraw,
    shouldStop: !showLimits,
  });

  useEffect(() => {
    return () => {
      dispatch(withdrawActions.clearAddressData());
    };
  }, []);

  useEffect(() => {
    if (getError('address')) dispatch(withdrawActions.clearErrors());

    const amount = rate?.amount;
    const fiat_currency = rate?.fiat_currency;
    const isValid =
      address?.isValid &&
      amount &&
      fiat_currency &&
      fromCurrency &&
      hasCurrencyWithdrawalFee &&
      !isLoadingEstimate &&
      address?.address;

    if (isValid && address?.address && isValidFee) {
      const data = {
        address: address?.address,
        amount: correctingTrailingNumbers(amount, displayDigits, totalDigits),
        currency: fromCurrency as CryptoCurrencyEnum,
        fiat_currency,
      };
      const promise = dispatch(withdrawActions.getWithdrawEstimate(data));
      abort.current = promise.abort;
      promise
        .unwrap()
        .then(res => {
          setWithdrawalFee(res.data.fees);
        })
        .catch(err => {
          setWithdrawalFee([]);
          const { name, message } = err;
          if (!err.data?.currency?.[0] && name !== 'AbortError') {
            const type = NotificationTypesEnum.error;
            dispatch(notificationsActions.add({ message, type }));
          }
        });
    }
  }, [
    address?.isValid,
    rate?.amount,
    fromCurrency,
    rate?.fiat_currency,
    address?.address,
    isValidFee,
  ]);

  useEffect(() => {
    const fee = withdrawalFee?.[0];
    if (withdrawalFee?.length && fee.fee && fee.is_paid_by_user) setFee(fee.id);
    else setFee();
  }, [withdrawalFee, rate, fromCurrency]);

  useEffect(() => {
    if (!fromAmount && !toAmount) clearWithdrawFee();
  }, [fromAmount, toAmount]);

  const getRate: TConverter['getRate'] = (from, to, amount) => {
    clearWithdrawFee();
    abort.current?.();
    return dispatch(withdrawActions.getWithdrawRate({ from, to, amount })).unwrap();
  };

  const onSubmit = () => {
    if (CONST.operationsDisabled) return;

    if (rate?.amount && address?.isValid && address?.address) {
      const data = {
        amount: correctingTrailingNumbers(rate.amount, displayDigits, totalDigits),
        currency: fromCurrency,
        address: address.address,
        estimate_id: withdraw.feeId,
        client: CONST.client,
      };
      dispatch(withdrawActions.withdraw(data))
        .unwrap()
        .then(res => {
          if (res.data.next === VerifyNextEnum.verifyEmail) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: `${CONST.nav.wallets}/${fromCurrency}`,
                flow: 'withdrawVerifyEmail',
                rate: rate as GETWithdrawRateResponse,
              },
            });
          }
        })
        .catch(err => {
          if (err.data.next === setupOTP) {
            navigate(CONST.nav.intro2fa);
          } else if (err.data.next === verifyOTP) {
            navigate<'verifications'>(CONST.nav.verification, {
              state: {
                nextPage: CONST.nav.root,
                flow: 'WithdrawVerify2FA',
              },
            });
          }
          return err;
        });
    }
  };

  const operationRate = rate && {
    ...rate,
    fee: {
      ...rate.fee,
      [fromCurrency]: withdrawalFee?.[0]?.fee || rate.fee?.[fromCurrency],
    },
    total: {
      ...rate.total,
      [fromCurrency]: withdrawalFee?.[0]?.total || rate.total?.[fromCurrency],
    },
  };

  const isTotalError = +operationRate?.total[left.value] > +wallet.data?.[left.value]?.balance;

  useEffect(() => {
    if (fromAmount || toAmount) updateRates();
  }, [left?.value, right?.value]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isTotalError) {
      const data = { type: NotificationTypesEnum.error, message: t('withdraw.total_error') };
      dispatch(notificationsActions.add(data));
      timeout = setTimeout(() => dispatch(notificationsActions.clear()), 3000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isTotalError]);

  const isSubmitDisabled =
    CONST.operationsDisabled ||
    isLoadingAddress ||
    !+fromAmount ||
    !+toAmount ||
    !rate?.amount ||
    !address?.isValid ||
    !!getError('address') ||
    (!withdrawalFee.length && hasCurrencyWithdrawalFee) ||
    isTotalError;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Converter getRate={getRate} {...converterState} isLoading={isLoadingRate} />
      <Limits limits={wallet.limits.withdraw} {...converterState} />
      <AddressInput currency={fromCurrency as CryptoCurrencyEnum} isLoading={!!addressLoading} />
      <Details from={fromCurrency} to={toCurrency} operationRate={operationRate} />
      <Button
        className={cn('button')}
        onClick={onSubmit}
        isLoading={isLoadingWithdraw || isLoadingEstimate}
        disabled={isSubmitDisabled}
        testId={ti('button')}
      >
        {t('transactions.withdraw')}
      </Button>
      {CONST.operationsDisabled && <OperationsDisabledDisclaimer />}
      {operationBlock.consentRequired !== 'zh' ? (
        <p className={cn('disclaimer')}>{t('withdrawal.disclaimer')}</p>
      ) : (
        <>
          <p className={cn('disclaimer')}>{t('zh.agreements.withdrawal')}</p>
          <ZHDisclaimerInfo className={cn('zh')} />
        </>
      )}
    </div>
  );
};

export default Withdraw;
